(() => {
  const STORAGE_KEY = 'geena-native-wishlist';
  const TOGGLE_SELECTOR = '[data-geena-wishlist-toggle]';

  function validHandles(value) {
    return Array.isArray(value)
      ? [...new Set(value.filter((handle) => typeof handle === 'string' && /^[a-z0-9][a-z0-9-]*$/i.test(handle)))]
      : [];
  }

  function readWishlist() {
    try {
      return { handles: validHandles(JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]')), available: true };
    } catch {
      return { handles: [], available: false };
    }
  }

  function writeWishlist(handles) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(validHandles(handles)));
      return true;
    } catch {
      return false;
    }
  }

  function announceWishlistChange() {
    window.dispatchEvent(new CustomEvent('geena:wishlist-change'));
  }

  function toggleLabel(button, saved) {
    const title = button.dataset.productTitle || 'this product';
    button.setAttribute('aria-pressed', String(saved));
    button.setAttribute('aria-label', `${saved ? 'Remove' : 'Save'} ${title} ${saved ? 'from' : 'to'} wishlist`);
    if (button.classList.contains('gq-wishlist-button')) button.textContent = saved ? '♥ SAVED' : '♡ SAVE';
  }

  function refreshToggles(scope = document) {
    const saved = new Set(readWishlist().handles);
    scope.querySelectorAll?.(TOGGLE_SELECTOR).forEach((button) => toggleLabel(button, saved.has(button.dataset.handle)));
  }

  function onToggle(event) {
    const button = event.target.closest(TOGGLE_SELECTOR);
    if (!button || !button.dataset.handle) return;
    const state = readWishlist();
    if (!state.available) {
      window.alert('Your browser does not allow saving. Please enable local storage to use your wishlist.');
      return;
    }
    const handles = new Set(state.handles);
    if (handles.has(button.dataset.handle)) handles.delete(button.dataset.handle);
    else handles.add(button.dataset.handle);

    if (!writeWishlist([...handles])) {
      window.alert('Your browser does not allow saving. Please enable local storage to use your wishlist.');
      return;
    }
    refreshToggles(document);
    announceWishlistChange();
  }

  if (!window.__geenaWishlistControls) {
    window.__geenaWishlistControls = true;
    document.addEventListener('click', onToggle);
    window.addEventListener('geena:wishlist-change', () => refreshToggles(document));
    window.addEventListener('storage', (event) => {
      if (event.key === STORAGE_KEY) {
        refreshToggles(document);
        window.dispatchEvent(new CustomEvent('geena:wishlist-change'));
      }
    });
  }

  function safeProductUrl(value, handle) {
    try {
      const url = new URL(value || `/products/${handle}`, window.location.origin);
      return url.origin === window.location.origin ? `${url.pathname}${url.search}${url.hash}` : `/products/${handle}`;
    } catch {
      return `/products/${handle}`;
    }
  }

  function safeImageUrl(value) {
    try {
      const url = new URL(value, window.location.origin);
      return url.protocol === 'https:' || url.protocol === 'http:' ? url.href : '';
    } catch {
      return '';
    }
  }

  function money(cents, currency, locale) {
    const amount = Number(cents);
    if (!Number.isFinite(amount)) return '';
    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency: currency || 'GBP' }).format(amount / 100);
    } catch {
      return `${currency || 'GBP'} ${(amount / 100).toFixed(2)}`;
    }
  }

  class GeenaWishlist extends HTMLElement {
    connectedCallback() {
      this.initialize();
    }

    disconnectedCallback() {
      this.destroy();
    }

    initialize() {
      if (this.initialized) return;
      this.initialized = true;
      this.grid = this.querySelector('[data-gw-grid]');
      this.status = this.querySelector('[data-gw-status]');
      this.note = this.querySelector('[data-gw-note]');
      this.onWishlistChange = this.refresh.bind(this);
      this.onRemove = this.onRemove.bind(this);
      window.addEventListener('geena:wishlist-change', this.onWishlistChange);
      this.addEventListener('click', this.onRemove);
      this.refresh();
    }

    destroy() {
      if (!this.initialized) return;
      this.abortController?.abort();
      window.removeEventListener('geena:wishlist-change', this.onWishlistChange);
      this.removeEventListener('click', this.onRemove);
      this.initialized = false;
    }

    onRemove(event) {
      const button = event.target.closest('[data-gw-remove]');
      if (!button || !this.contains(button)) return;
      const handle = button.dataset.gwRemove;
      const state = readWishlist();
      if (!state.available || !writeWishlist(state.handles.filter((saved) => saved !== handle))) {
        window.alert('Your browser does not allow changing this device-local wishlist.');
        return;
      }
      announceWishlistChange();
    }

    setStatus(message) {
      if (this.status) this.status.textContent = message;
    }

    async refresh() {
      if (!this.initialized || !this.grid) return;
      this.abortController?.abort();
      this.abortController = new AbortController();
      const signal = this.abortController.signal;
      const state = readWishlist();
      const locale = document.documentElement.lang || undefined;
      const currency = this.dataset.currency || 'GBP';
      this.grid.replaceChildren();
      this.grid.setAttribute('aria-busy', 'true');

      if (!state.available) {
        this.setStatus('This browser does not allow device-local wishlist storage.');
        this.renderEmpty('Your browser does not allow saving a device-local wishlist.');
        this.grid.setAttribute('aria-busy', 'false');
        return;
      }
      if (!state.handles.length) {
        this.setStatus('Your wishlist is empty.');
        this.renderEmpty();
        this.grid.setAttribute('aria-busy', 'false');
        return;
      }

      this.setStatus('Loading your saved styles…');
      const settled = await Promise.all(state.handles.map(async (handle) => {
        try {
          const response = await fetch(`/products/${encodeURIComponent(handle)}.js`, {
            credentials: 'same-origin',
            headers: { Accept: 'application/json' },
            signal,
          });
          if (!response.ok) throw new Error(`Product returned ${response.status}`);
          const product = await response.json();
          if (!product || typeof product !== 'object' || !product.handle) throw new Error('Invalid product response');
          return { handle, product };
        } catch (error) {
          if (error?.name === 'AbortError') throw error;
          return { handle, error };
        }
      })).catch((error) => {
        if (error?.name === 'AbortError') return null;
        return [];
      });
      if (!settled || signal.aborted || !this.initialized) return;

      const products = settled.filter((result) => result.product);
      const unavailable = settled.filter((result) => !result.product).map((result) => result.handle);
      products.forEach(({ handle, product }) => this.grid.append(this.productCard(product, handle, currency, locale)));

      if (!products.length) this.renderEmpty('We could not load any saved products from the public catalogue.');
      if (unavailable.length) this.renderUnavailable(unavailable);
      const availableCount = products.filter(({ product }) => product.available === true).length;
      this.setStatus(`${products.length} saved ${products.length === 1 ? 'style' : 'styles'} loaded. ${availableCount} currently available.${unavailable.length ? ` ${unavailable.length} could not be loaded.` : ''}`);
      this.grid.setAttribute('aria-busy', 'false');
      if (this.note) this.note.hidden = false;
    }

    renderEmpty(message) {
      const empty = document.createElement('div');
      empty.className = 'gw-empty';
      const heading = document.createElement('h2');
      heading.innerHTML = 'A place for the pieces<br><em>you love.</em>';
      const copy = document.createElement('p');
      copy.textContent = message || 'Save a style with the heart button and return to it here. Your wishlist lives on this device, without an account.';
      const link = document.createElement('a');
      link.className = 'gw-button';
      link.href = this.dataset.collectionUrl || '/collections/all';
      link.textContent = 'EXPLORE THE COLLECTION →';
      empty.append(heading, copy, link);
      this.grid.append(empty);
      if (this.note) this.note.hidden = false;
    }

    renderUnavailable(handles) {
      const aside = document.createElement('aside');
      aside.className = 'gw-unavailable';
      const title = document.createElement('h2');
      title.textContent = 'Some saved styles are no longer available to load.';
      const copy = document.createElement('p');
      copy.textContent = 'They have not been represented as products. You can remove their saved handles from this device.';
      const list = document.createElement('ul');
      handles.forEach((handle) => {
        const item = document.createElement('li');
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'gw-remove';
        button.dataset.gwRemove = handle;
        button.textContent = `Remove ${handle} from wishlist`;
        item.append(button);
        list.append(item);
      });
      aside.append(title, copy, list);
      this.grid.append(aside);
    }

    productCard(product, handle, currency, locale) {
      const card = document.createElement('article');
      card.className = 'gw-card';
      const url = safeProductUrl(product.url, handle);
      const imageLink = document.createElement('a');
      imageLink.className = 'gw-card-image';
      imageLink.href = url;
      const imageUrl = safeImageUrl(product.featured_image || product.images?.[0]);
      if (imageUrl) {
        const image = document.createElement('img');
        image.src = imageUrl;
        image.alt = product.title || '';
        image.loading = 'lazy';
        image.width = 800;
        image.height = 1000;
        imageLink.append(image);
      } else {
        const placeholder = document.createElement('span');
        placeholder.className = 'gw-placeholder';
        placeholder.setAttribute('aria-hidden', 'true');
        placeholder.textContent = 'GEENA';
        imageLink.append(placeholder);
      }

      const detail = document.createElement('div');
      detail.className = 'gw-card-detail';
      if (product.type) {
        const type = document.createElement('p');
        type.className = 'gw-card-type';
        type.textContent = product.type;
        detail.append(type);
      }
      const title = document.createElement('h2');
      const titleLink = document.createElement('a');
      titleLink.href = url;
      titleLink.textContent = product.title || handle;
      title.append(titleLink);
      const price = document.createElement('p');
      price.className = 'gw-card-price';
      price.textContent = money(product.price_min ?? product.price, currency, locale);
      const stock = document.createElement('p');
      stock.className = 'gw-stock';
      stock.textContent = product.available === true ? 'Available now' : product.available === false ? 'Sold out' : 'Availability unavailable';
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'gw-remove';
      remove.dataset.gwRemove = handle;
      remove.textContent = `Remove ${product.title || handle} from wishlist`;
      detail.append(title, price, stock, remove);
      card.append(imageLink, detail);
      return card;
    }
  }

  if (!customElements.get('geena-native-wishlist')) customElements.define('geena-native-wishlist', GeenaWishlist);

  if (!window.__geenaWishlistSectionEvents) {
    window.__geenaWishlistSectionEvents = true;
    document.addEventListener('shopify:section:load', (event) => {
      event.target.querySelectorAll?.('geena-native-wishlist').forEach((wishlist) => wishlist.initialize?.());
    });
    document.addEventListener('shopify:section:unload', (event) => {
      event.target.querySelectorAll?.('geena-native-wishlist').forEach((wishlist) => wishlist.destroy?.());
    });
  }
})();
